'use client';
import React, { useRef, useEffect } from 'react';
import tippy, { Instance } from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import { UserShortCutType } from 'components/types';
import { UserShortCutService } from 'services/UserShortCutService';
import { useSidebarManager } from 'contexts/SidebarManagerContext';
import IndexChat from 'features/chat/components/IndexChat';
import { hasPermission } from 'utils/permissions';
import { useAuthContext } from 'contexts/AuthContext';
import { useTranslation } from 'react-i18next';

interface UserShortCutProps {
  userId?: string;
  userPath?: string;
  src?: string;
  alt?: string;
  className?: string;
  parentClass?: string;
  secondContent?: string;
  /** Custom trigger (e.g. a kit-styled Avatar). When provided it replaces the default <img>, so the
   *  same hover profile-card behaviour can wrap ANY avatar visual. */
  children?: React.ReactNode;
}

const UserShortCutContent = (
  data: UserShortCutType,
  imgSrc: string,
  alt: string,
  showChatButton: boolean,
  profileUrl: string,
): string => {
  return `
    <div class="minthr-flex minthr-flex-items-center">
      <div class="minthr-flex minthr-flex-space-start minthr-flex-items-center">
        <img
          class="minthr-avatar minthr-avatar-m shortcut-profilePicture"
          src="${imgSrc}"
          alt="${alt}"
        />
        <div class="minthr-flex minthr-flex-column minthr-ml-2">
          <a
            class="minthr-line-height-10 shortcutt-fullname minthr-color-dark"
            href="${profileUrl}"
            target="_blank"
            rel="noopener noreferrer"
            style="text-decoration: none; color: inherit;"
          >
            ${data.fullName}
          </a>
          <h6 class="minthr-color-gray minthr-line-height-10 shortcutt-departement">
            ${data.departement ?? ''}
          </h6>
        </div>
      </div>
      ${
        showChatButton
          ? `<button class="minthr-btn minthr-btn-secondary minthr-btn-s minthr-ml-auto shortcut-ChatBubble">
              <img
                class="minthr-icon-s"
                src="/icons/chat-bubble.svg"
                alt="chat-bubble"
              />
            </button>`
          : ''
      }
      <a
        class="minthr-btn minthr-btn-primary minthr-btn-s ${
          showChatButton ? 'minthr-ml-1' : 'minthr-ml-auto'
        } shortcut-ProfilRedirect"
        href="${profileUrl}"
        target="_blank"
        rel="noopener noreferrer"
      >
        <img class="minthr-icon-s" src="/icons/arrow.svg" alt="arrow" />
      </a>
    </div>
  `;
};

const UserShortCut: React.FC<UserShortCutProps> = ({
  userId,
  userPath,
  src,
  alt = '',
  className = '',
  parentClass = '',
  secondContent,
  children,
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const avatarRef = useRef<HTMLImageElement>(null);
  const tippyInstanceRef = useRef<Instance | null>(null);
  const tippySecondRef = useRef<Instance | null>(null);
  const loadingRef = useRef<boolean>(false);
  const { openSidebar } = useSidebarManager();
  const { user } = useAuthContext();

  const showChatButton =
    hasPermission('Chat.View') && user.chatAccessLevel !== 'DisableChat';

  const imgSrc = userId
    ? `/upload/profileimages/${userId}/${userPath}`
    : src
      ? '/' + src
      : '/icons/Group 65.svg';

  const handleShow = async (instance: Instance): Promise<void> => {
    if (loadingRef.current) return;

    loadingRef.current = true;

    try {
      const tooltipContent = instance.popper.querySelector('.tippy-content');
      if (tooltipContent) {
        tooltipContent.classList.add(
          'minthr-box-shadow',
          'minthr-bg-white',
          'minthr-p-3',
          'minthr-user-info-shortcut',
        );
      }

      instance.setContent(`
        <div class="minthr-shortcut-skeleton-card minthr-bg-white minthr-h-100 minthr-w-100 ">
          <div class="minthr-shortcut-skeleton minthr-shortcut-skeleton-circle"></div>
          <div class="minthr-shortcut-skeleton-content">
            <div class="minthr-shortcut-skeleton minthr-shortcut-skeleton-row-lg"></div>
            <div class="minthr-shortcut-skeleton minthr-shortcut-skeleton-row-sm"></div>
          </div>
          <div class="minthr-shortcut-skeleton minthr-shortcut-skeleton-button"></div>
        </div>
      `);

      let id;
      if (userId) {
        id = userId;
      } else {
        const parts = (src ?? '').split('/');
        id = parts[2];
      }
      const data = await UserShortCutService(id);

      if (data.success) {
        const profileUrl = `/employee/${data.data.id}/profile/public`;
        instance.setContent(
          UserShortCutContent(
            data.data,
            imgSrc,
            alt,
            showChatButton,
            profileUrl,
          ),
        );

        const chatBtn = instance.popper.querySelector('.shortcut-ChatBubble');

        if (chatBtn) {
          chatBtn.addEventListener('click', () => {
            openSidebar(IndexChat, true);
          });
        }
      } else {
        instance.destroy();
      }
    } catch (error) {
      console.error('Erreur lors du chargement du user:', error);
      instance.setContent(t('User.notFound'));
    } finally {
      loadingRef.current = false;
    }
  };

  useEffect(() => {
    if (!containerRef.current) return;

    tippyInstanceRef.current = tippy(containerRef.current, {
      placement: 'top',
      allowHTML: true,
      interactive: true,
      theme: 'custom',
      appendTo: document.body,
      trigger: 'mouseenter focus',
      hideOnClick: false,
      interactiveBorder: 10,
      animation: false,
      delay: [1000, 0],
      onCreate(instance) {
        instance.popper.classList.add('minthr-hide');
      },
      onShow(instance) {
        handleShow(instance);
        instance.popper.classList.remove('minthr-hide');
      },
      onHide() {
        loadingRef.current = false;
      },
    });

    if (secondContent && secondContent != ' ' && avatarRef.current) {
      tippySecondRef.current = tippy(avatarRef.current, {
        content: secondContent,
        placement: 'top',
        theme: 'light',
      });
    }

    return () => {
      tippyInstanceRef.current?.destroy();
      tippySecondRef.current?.destroy();
    };
  }, [userId, userPath, src, secondContent]);

  return (
    <div
      ref={containerRef}
      className={parentClass}
      style={{ position: 'relative', display: 'inline-block' }}
    >
      <div className="minthr-flex minthr-flex-items-center minthr-flex-justify-center">
        {children ?? <img ref={avatarRef} className={className} src={imgSrc} alt={alt} />}
      </div>
    </div>
  );
};

export default UserShortCut;
